import { PhotoGrid } from '../components/PhotoLightbox';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, FileWarning, MapPin, CalendarDays, ChevronRight, Phone, Map, Sparkles, ClipboardCheck, BarChart3, ShieldCheck, Lock, Radar, TrendingUp, Quote, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { api, fileUrl } from '../lib/api';
import { PageHeader, SectionHeader, Loading, EmptyState, StatusBadge, formatDate, ISSUE_CATEGORIES, categoryLabel, STATUS_LABELS, useIssueCategories, RichSection } from '../components/Shared';
import { NextStepBand } from '../components/NextStepBand';
import { HopeInlineSection, useHopeTheme } from '../components/HopeTheme';
import { useCampaignContent, TaxPromiseSection, ReportTrackBlock } from '../components/CampaignBlocks';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { PitaShareDialog, usePitaLogo } from '../components/PitaCTA';

export const HUB_DISCLAIMER = 'This is not an official City of Kawartha Lakes portal. Residents may still need to submit official requests through CKL ServiceHub, Report It, Customer Service, By-law Complaint Form, or other official City channels.';

export const HubDisclaimer = ({ testId = 'hub-disclaimer' }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
    <ShieldCheck className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
    <p className="text-xs sm:text-sm text-amber-900 leading-relaxed" data-testid={testId}>{HUB_DISCLAIMER}</p>
  </div>
);

const IssueTimeline = ({ issue }) => (
  <div className="space-y-0" data-testid="issue-status-timeline">
    {(issue.updates || []).slice().reverse().map((u, i, arr) => (
      <div key={u.id} className="flex gap-3">
        <div className="flex flex-col items-center">
          <span className={`h-3.5 w-3.5 rounded-full shrink-0 mt-1 ${i === 0 ? 'bg-[#C9A84C]' : 'bg-slate-300'}`} />
          {i < arr.length - 1 && <span className="w-0.5 flex-1 bg-slate-200" />}
        </div>
        <div className="pb-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={u.status} />
            <span className="text-xs text-slate-500">{formatDate(u.date)}</span>
          </div>
          <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{u.note}</p>
        </div>
      </div>
    ))}
  </div>
);

const IssueDetail = ({ issue, onClose }) => (
  <Dialog open={!!issue} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="issue-detail-dialog">
      {issue && (
        <>
          <DialogHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-bold text-[#C9A84C]" data-testid="issue-detail-tracking">{issue.tracking_number}</span>
              <StatusBadge status={issue.status} />
              {issue.days_open > 0 && <span className="text-xs text-slate-500">{issue.days_open} day(s) open</span>}
            </div>
            <DialogTitle className="font-serif text-[#1a2744] text-left leading-snug">{issue.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700 leading-relaxed">{issue.description}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              <span>{categoryLabel(issue.category)}</span>
              {(issue.community || issue.location) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {issue.community || issue.location}</span>}
              <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Reported {formatDate(issue.created_at)}</span>
            </div>
            {issue.photos?.length > 0 && (
              <PhotoGrid photos={issue.photos} caption={issue.tracking_number} testId="track-photos" />
            )}
            <div className="border-t pt-4">
              <p className="font-bold text-sm text-[#1a2744] mb-3">Status Timeline</p>
              <IssueTimeline issue={issue} />
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const HOW_IT_WORKS = [
  { icon: FileWarning, title: 'Report the issue', body: 'Fill in the quick step-by-step form and get a W8 tracking number instantly.' },
  { icon: ShieldCheck, title: 'Reviewed before it goes public', body: 'The campaign reviews every report and uses privacy-safe summaries before anything is shown publicly.' },
  { icon: Phone, title: 'Find the proper CKL contact path', body: 'Get pointed to the right official City channel: ServiceHub, Report It, or the right department.' },
  { icon: ClipboardCheck, title: 'Track follow-up', body: 'Check your report any time, add updates, and record your CKL case number.' },
  { icon: BarChart3, title: 'See public accountability patterns', body: 'Public-safe reports appear on the issue board and map so Ward 8 can see what is happening.' },
];

export default function ActionHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const liveCategories = useIssueCategories();
  const hopeTheme = useHopeTheme();
  const campaign = useCampaignContent();
  const tp = campaign?.tracker_page || {};
  const featureChips = (tp.chips || '').split('\n').map((c) => c.trim()).filter(Boolean);
  const [net, setNet] = useState(null);
  useEffect(() => { api.get('/network/content').then((r) => setNet(r.data)).catch(() => {}); }, []);

  // App preview block — the tracker as a real working tool (admin-editable placement)
  const appBullets = (tp.app_bullets || '').split('\n').map((b) => b.trim()).filter(Boolean);
  const appPreview = tp.app_show !== false ? (
    <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/70 p-6 sm:p-8" data-testid="tracker-app-preview">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="lg:col-span-7 min-w-0">
          {tp.app_kicker && <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A84C]" data-testid="tracker-app-kicker">{tp.app_kicker}</p>}
          <h2 className="font-serif font-black text-[#1a2744] text-2xl sm:text-3xl leading-tight mt-2" data-testid="tracker-app-title">{tp.app_title}</h2>
          {tp.app_subtitle && <p className="text-slate-600 mt-2 leading-relaxed" data-testid="tracker-app-subtitle">{tp.app_subtitle}</p>}
          <div className="mt-3 space-y-2.5">
            {(tp.app_body || '').split('\n\n').map((p) => p.trim()).filter(Boolean).map((p, i) => (
              <p key={i} className="text-sm sm:text-base text-slate-700 leading-relaxed">{p}</p>
            ))}
          </div>
          {tp.app_callout && (
            <p className="mt-4 border-l-4 border-[#C9A84C] pl-3.5 font-serif font-bold text-[#1a2744]" data-testid="tracker-app-callout">{tp.app_callout}</p>
          )}
          {appBullets.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="tracker-app-bullets">
              {appBullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#C9A84C] shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-3">
            {tp.app_primary_label && (
              <Button asChild className="h-12 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full sm:w-auto justify-center" data-testid="tracker-app-primary-cta">
                <Link to={tp.app_primary_url || '/issue-tracker'}><ExternalLink className="h-4 w-4 mr-1.5" /> {tp.app_primary_label}</Link>
              </Button>
            )}
            {tp.app_secondary_label && (
              <Button asChild variant="outline" className="h-12 px-5 border-[#1a2744] text-[#1a2744] font-bold btn-press w-full sm:w-auto justify-center" data-testid="tracker-app-secondary-cta">
                <Link to={tp.app_secondary_url || '/issue-tracker/report'}><FileWarning className="h-4 w-4 mr-1.5" /> {tp.app_secondary_label}</Link>
              </Button>
            )}
          </div>
        </div>
        {tp.app_image && (
          <div className="lg:col-span-5 min-w-0">
            <img src={fileUrl(tp.app_image)} alt={tp.app_image_alt || 'Ward 8 Issue Tracker App (PITA) preview'} className="w-full max-w-[260px] sm:max-w-xs mx-auto rounded-2xl shadow-xl ring-1 ring-slate-200 bg-white" data-testid="tracker-app-image" />
          </div>
        )}
      </div>
    </div>
  ) : null;
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [trackNum, setTrackNum] = useState(searchParams.get('track') || '');
  const [trackResult, setTrackResult] = useState(null);
  const [trackError, setTrackError] = useState('');
  const [detail, setDetail] = useState(null);

  const loadIssues = useCallback(() => {
    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    if (category !== 'all') params.set('category', category);
    if (search) params.set('search', search);
    api.get(`/issues?${params}`).then((r) => setIssues(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [status, category, search]);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  const doTrack = useCallback(async (num) => {
    const n = (num || '').trim().toUpperCase();
    if (!n) return;
    setTrackError('');
    setTrackResult(null);
    try {
      const r = await api.get(`/issues/track/${encodeURIComponent(n)}`);
      setTrackResult(r.data);
    } catch (err) {
      setTrackError(err.response?.data?.detail || 'No report found with that tracking number.');
    }
  }, []);

  useEffect(() => {
    const t = searchParams.get('track');
    if (t) { setTrackNum(t); doTrack(t); }
  }, [searchParams, doTrack]);

  const pitaLogo = usePitaLogo('pita_page');

  return (
    <div id="overview" className="scroll-mt-24">
      <PageHeader
        pageKey="pita"
        title={'GOT AN ISSUE?\nPUT IT IN PITA!'}
        subtitle="Report It. Map It. Track It. Get Answers."
      />

      {/* Slogan band */}
      <div className="bg-[#eaf2ff] border-b border-[#24406f]/15">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="font-serif font-bold text-[#1a2744] text-sm sm:text-base leading-snug" data-testid="hub-slogan">
            One complaint can be overlooked. A map full of complaints cannot.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <RichSection id="pita-sections.overview_intro" className="mb-6" />
        {/* CTA buttons */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Button asChild className="h-14 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold btn-press text-sm sm:text-base" data-testid="hub-cta-report">
            <Link to="/issue-tracker/report"><FileWarning className="h-4 w-4 mr-1.5" /> Report an Issue</Link>
          </Button>
          <Button asChild className="h-14 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press text-sm sm:text-base" data-testid="hub-cta-track">
            <Link to="/issue-tracker/track"><Search className="h-4 w-4 mr-1.5" /> Track My Issue</Link>
          </Button>
          <Button asChild variant="outline" className="h-14 border-[#1a2744] text-[#1a2744] font-bold btn-press text-sm sm:text-base" data-testid="hub-cta-map">
            <Link to="/map"><Map className="h-4 w-4 mr-1.5" /> View Public Map</Link>
          </Button>
          <Button asChild variant="outline" className="h-14 border-[#1a2744] text-[#1a2744] font-bold btn-press text-sm sm:text-base" data-testid="hub-cta-whocall">
            <Link to="/who-do-i-call"><Phone className="h-4 w-4 mr-1.5" /> Who Do I Call?</Link>
          </Button>
        </div>

        <HubDisclaimer testId="action-hub-disclaimer" />

        {/* Feature chips */}
        {featureChips.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="tracker-page-chips">
            {featureChips.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-[#1a2744]/[0.05] border border-[#1a2744]/15 px-3 py-1.5 text-[11px] sm:text-xs font-bold text-[#1a2744]">
                <MapPin className="h-3 w-3 text-[#C9A84C]" /> {c}
              </span>
            ))}
          </div>
        )}

        {/* Punch line: put it on the map */}
        {tp.punch_map && (
          <p className="mt-6 font-serif font-black text-[#1a2744] text-lg sm:text-xl" data-testid="tracker-punch-map">
            {tp.punch_map}
          </p>
        )}

        {/* App preview — position: after intro */}
        {(tp.app_position || 'before_why') === 'after_intro' && appPreview}

        {/* Lesperance is the French word for Hope — Hope You Can Track */}
        <HopeInlineSection block={hopeTheme.hub} testid="hope-hub" />

        {/* How it works */}
        <div className="mt-10">
          <SectionHeader kicker="HOW IT WORKS" title="Five steps to real follow-up" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="hub-how-it-works">
            {HOW_IT_WORKS.map((s, i) => (
              <Card key={s.title} className="card-lift">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#1a2744] text-[#C9A84C] font-black text-sm shrink-0">{i + 1}</span>
                    <s.icon className="h-5 w-5 text-[#C9A84C]" />
                  </div>
                  <p className="font-serif font-bold text-[#1a2744] mt-3 leading-snug">{s.title}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* What residents can report */}
        <div className="mt-10">
          <SectionHeader kicker="WHAT RESIDENTS CAN REPORT" title="If it affects Ward 8, report it" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hub-report-categories">
            {Object.entries(liveCategories).map(([v, l]) => (
              <Link key={v} to={`/issue-tracker/report?category=${v}`} data-testid={`hub-category-card-${v}`}>
                <Card className="card-lift h-full">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <p className="font-semibold text-[#1a2744] text-sm leading-snug">{l}</p>
                    <ChevronRight className="h-4 w-4 text-[#C9A84C] shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* What makes it different — privacy / visibility / scale */}
        <div className="mt-10">
          <SectionHeader kicker="WHAT MAKES IT DIFFERENT" title="Public. Privacy-safe. Built to scale." dek={tp.punch_public} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="tracker-page-pillars">
            <Card className="card-lift h-full" data-testid="tracker-pillar-privacy">
              <CardContent className="p-5">
                <Lock className="h-6 w-6 text-[#C9A84C]" />
                <p className="font-serif font-bold text-[#1a2744] mt-3 leading-snug">{tp.privacy_title}</p>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{tp.privacy_body}</p>
              </CardContent>
            </Card>
            <Card className="card-lift h-full" data-testid="tracker-pillar-visibility">
              <CardContent className="p-5">
                <Radar className="h-6 w-6 text-[#C9A84C]" />
                <p className="font-serif font-bold text-[#1a2744] mt-3 leading-snug">{tp.visibility_title}</p>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{tp.visibility_body}</p>
              </CardContent>
            </Card>
            <Card className="card-lift h-full" data-testid="tracker-pillar-scale">
              <CardContent className="p-5">
                <TrendingUp className="h-6 w-6 text-[#C9A84C]" />
                <p className="font-serif font-bold text-[#1a2744] mt-3 leading-snug">{tp.scale_title}</p>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{tp.scale_body}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* App preview — position: before "Why this matters" (default) */}
        {(tp.app_position || 'before_why') === 'before_why' && appPreview}

        {/* Why this matters (admin-editable) */}
        <div className="mt-10 bg-[#1a2744] rounded-2xl p-6 sm:p-10" data-testid="hub-why-matters">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#a9c6ff] mb-2">{tp.why_kicker || 'WHY THIS MATTERS'}</p>
          <RichSection id="pita-sections.why_matters" className="mb-3 [&_p]:text-slate-300 [&_h2]:text-white [&_h3]:text-white" />
          <p className="text-white text-lg sm:text-xl font-serif font-bold leading-relaxed max-w-3xl">
            {tp.why_title}
          </p>
          <div className="mt-3 space-y-2.5 max-w-3xl">
            {(tp.why_body || '').split('\n\n').map((p) => p.trim()).filter(Boolean).map((p, i) => (
              <p key={i} className="text-slate-300 leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-3">
            <Button asChild className="h-12 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press w-full sm:w-auto justify-center" data-testid="tracker-why-cta-report">
              <Link to="/issue-tracker/report"><FileWarning className="h-4 w-4 mr-1.5" /> Report a Ward 8 Issue</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press w-full sm:w-auto justify-center" data-testid="tracker-why-cta-map">
              <Link to="/map"><Map className="h-4 w-4 mr-1.5" /> View the Public Issue Map</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press w-full sm:w-auto justify-center" data-testid="tracker-why-cta-track">
              <Link to="/issue-tracker/track"><Search className="h-4 w-4 mr-1.5" /> Track My Issue</Link>
            </Button>
          </div>
        </div>

        {/* App preview — position: after "Why this matters" */}
        {(tp.app_position || 'before_why') === 'after_why' && appPreview}

        {/* Punch line: proof */}
        {tp.punch_proof && (
          <p className="mt-8 font-serif font-black text-[#1a2744] text-lg sm:text-xl" data-testid="tracker-punch-proof">
            {tp.punch_proof}
          </p>
        )}

        {/* Taxpayer value + careful software-cost caution */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="h-full border-[#C9A84C]/50" data-testid="tracker-page-value">
            <CardContent className="p-6">
              <BarChart3 className="h-6 w-6 text-[#C9A84C]" />
              <h3 className="font-serif font-black text-[#1a2744] text-lg mt-3 leading-snug">{tp.value_title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{tp.value_body}</p>
            </CardContent>
          </Card>
          {tp.caution_show !== false && (
            <Card className="h-full bg-slate-50" data-testid="tracker-page-caution">
              <CardContent className="p-6">
                <AlertTriangle className="h-6 w-6 text-slate-400" />
                <h3 className="font-serif font-bold text-[#1a2744] text-lg mt-3 leading-snug">{tp.caution_title}</h3>
                <p className="text-xs sm:text-sm text-slate-500 mt-2 leading-relaxed">{tp.caution_body}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Reported public statement — small, source-safe supporting block */}
        {tp.ron_show !== false && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="tracker-page-ron">
            <div className="flex items-center gap-2">
              <Quote className="h-4 w-4 text-[#C9A84C] shrink-0" />
              <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-slate-500" data-testid="tracker-ron-kicker">{tp.ron_kicker || 'REPORTED PUBLIC STATEMENT'}</p>
            </div>
            <p className="font-serif font-bold text-[#1a2744] mt-2 leading-snug">{tp.ron_title}</p>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{tp.ron_body}</p>
            {tp.ron_note && <p className="text-[11px] sm:text-xs text-slate-400 italic mt-2.5 pt-2.5 border-t border-slate-100" data-testid="tracker-ron-note">{tp.ron_note}</p>}
          </div>
        )}

        {/* Quick track */}
        <Card className="mt-10" id="track">
          <CardContent className="p-6 sm:p-8">
            <Search className="h-8 w-8 text-[#1a2744] mb-2" />
            <h2 className="font-serif font-black text-[#1a2744] text-xl">Quick status check</h2>
            <p className="text-slate-600 mt-1 text-sm">Enter a W8 tracking number for a public status view, or use <Link to="/issue-tracker/track" className="font-bold underline decoration-[#C9A84C] decoration-2 underline-offset-4">Track My Issue</Link> to securely manage your own report.</p>
            <form onSubmit={(e) => { e.preventDefault(); setSearchParams({ track: trackNum }); doTrack(trackNum); }} className="flex flex-col sm:flex-row gap-2 mt-4" data-testid="track-form">
              <Input value={trackNum} onChange={(e) => setTrackNum(e.target.value)} placeholder="W8-2026-0001" className="h-12 font-mono sm:flex-1 shrink-0" data-testid="track-input" />
              <Button type="submit" className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white btn-press" data-testid="track-submit">Track</Button>
            </form>
            {trackError && <p className="text-sm text-red-700 mt-3 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="track-error">{trackError}</p>}
            {trackResult && (
              <div className="mt-4 border border-slate-200 rounded-xl p-4" data-testid="track-result">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-mono text-sm font-bold text-[#C9A84C]">{trackResult.tracking_number}</span>
                  <StatusBadge status={trackResult.status} />
                </div>
                <p className="font-bold text-[#1a2744] mt-2">{trackResult.title}</p>
                <div className="mt-3"><IssueTimeline issue={trackResult} /></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Public issue board */}
        <div className="mt-10">
          <SectionHeader kicker="PUBLIC ISSUE BOARD" title="What Ward 8 is reporting" dek="Public reports are listed with consent. Names, contact details, and exact addresses are never shown." />
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reports…" className="h-12 bg-white sm:max-w-xs" data-testid="issue-board-search-input" />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-12 bg-white sm:w-48" data-testid="issue-board-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12 bg-white sm:w-60" data-testid="issue-board-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {Object.entries(liveCategories).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? <Loading /> : issues.length === 0 ? (
            <EmptyState title="No reports match your filters" subtitle="Try adjusting the filters, or be the first to report an issue." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {issues.map((issue) => (
                <button key={issue.id} onClick={() => setDetail(issue)} className="text-left" data-testid="issue-board-issue-card">
                  <Card className="card-lift h-full">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold text-[#C9A84C]">{issue.tracking_number}</span>
                        <StatusBadge status={issue.status} />
                      </div>
                      <p className="font-serif font-bold text-[#1a2744] mt-2 leading-snug line-clamp-2">{issue.title}</p>
                      <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{issue.description}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-xs text-slate-500">
                        <span>{categoryLabel(issue.category)}</span>
                        {(issue.community || issue.location) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {issue.community || issue.location}</span>}
                        {issue.days_open > 0 && <span>{issue.days_open}d open</span>}
                      </div>
                    </CardContent>
                  </Card>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bring This to Your Ward — PITA Network promotion */}
        {net && (
          <div className="mt-12 rounded-2xl bg-[#1a2744] p-6 sm:p-10 relative overflow-hidden" data-testid="tracker-network-promo">
            <div aria-hidden="true" className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)', backgroundSize: '56px 56px' }} />
            <div className="relative">
              <div className="flex items-start gap-4">
                {pitaLogo && (
                  <div className="bg-white rounded-xl p-2 shrink-0 h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center" data-testid="promo-pita-logo">
                    <img src={pitaLogo.url} alt={pitaLogo.alt} className="max-h-full max-w-full object-contain" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-widest text-[#C9A84C]">PUBLIC ISSUE TRACKER APP (PITA) NETWORK</p>
                  <h2 className="text-white font-serif font-black text-2xl sm:text-3xl mt-2" data-testid="network-promo-title">{net.promo_title}</h2>
                </div>
              </div>
              <p className="text-slate-300 mt-3 max-w-2xl leading-relaxed" data-testid="network-promo-body">{net.promo_body}</p>
              <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
                <PitaShareDialog
                  trigger={
                    <Button className="h-12 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press justify-center" data-testid="promo-share-btn">
                      Share PITA
                    </Button>
                  }
                />
                <Button asChild variant="outline" className="h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-center" data-testid="promo-embed-btn">
                  <Link to="/pita/request-access">Request PITA Embed</Link>
                </Button>
                <Button asChild variant="outline" className="h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-center" data-testid="promo-support-btn">
                  <Link to="/support-pita">Support PITA</Link>
                </Button>
                <Button asChild variant="outline" className="h-12 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press justify-center" data-testid="promo-supporters-btn">
                  <Link to="/pita-supporters">View Supporters by Ward</Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Campaign flyer download */}
        <div className="mt-8"><FlyerDownloadSection compact loc="issue-tracker" /></div>
      </div>

      {/* Report It. Track It. Cut the Waste. Lower the Taxes. */}
      <ReportTrackBlock promise={campaign?.tax_promise} />
      {campaign?.tax_promise?.sections?.hub_tax_promise !== false && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <TaxPromiseSection promise={campaign?.tax_promise} compact />
        </div>
      )}

      {/* One clear next step */}
      <NextStepBand
        kicker="GOT AN ISSUE?"
        title="Put It In PITA!"
        buttonLabel="Report an Issue"
        to="/issue-tracker/report"
        secondaryLabel="Or track an issue you already reported"
        secondaryTo="/issue-tracker/track"
        testId="pita-next-step"
      />

      <IssueDetail issue={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
